import { useMutation, useQueryClient } from '@tanstack/react-query';
import { offerPricing } from '@ooh/contracts';
import type { BatchUpdatePricesRequest } from '@ooh/contracts';
import { rethrowOfferConflict } from './errors';
import { offerKeys, offerPricingKeys, useEffectiveAuth } from './queries';
import type { WithCallWithAuth } from './queries';
// The merged table's price/discount/lineTotal columns read `OfferUnitListItem`
// rows (`GET /offers/:offerId/units`), not a separate price listing — a
// successful price mutation must therefore invalidate that query prefix too,
// or the merged table would keep showing stale price cells until an
// unrelated refetch happened to occur.
import { offerUnitQueries } from '../../units-tab/api/queries';

export function useBatchUpdatePrices(offerId: number, auth?: WithCallWithAuth) {
  const effectiveAuth = useEffectiveAuth(auth);
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, BatchUpdatePricesRequest>({
    mutationFn: async (body) => {
      try {
        return await effectiveAuth.callWithAuth(offerPricing.batchUpdate, { offerId, ...body });
      } catch (error) {
        rethrowOfferConflict(error);
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: offerUnitQueries.all(offerId) }),
        queryClient.invalidateQueries({ queryKey: offerPricingKeys.totals(offerId) }),
        queryClient.invalidateQueries({ queryKey: offerKeys.detail(String(offerId)) }),
      ]);
    },
  });
}
